import React, { useState } from "react";
import { AppShell } from "@design-system/AppShell/AppShell";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { VStack } from "@design-system/VStack/VStack";
import {
    AppShellPlaygroundNavigation,
    AppShellPlaygroundRoute,
} from "./AppShellPlaygroundNavigation";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import layoutStyles from "./structureLayoutControls.module.scss";
import styles from "./AppShellPlayground.module.scss";

const modes = ["default", "contained", "fullPage"] as const;
type Mode = (typeof modes)[number];

const modeDetails: Record<
    Mode,
    { label: string; route: AppShellPlaygroundRoute; summary: string }
> = {
    default: {
        label: "Default",
        route: "/accounts",
        summary:
            "Main grows with its content and the page scrolls, so the region ends where the content does.",
    },
    contained: {
        label: "Contained",
        route: "/transactions",
        summary:
            "The shell is capped at calc(100vh - 160px) for pages that scroll internally. The content column keeps min-height: 100vh, so the tinted band marks layout that continues past the cap.",
    },
    fullPage: {
        label: "Full page",
        route: "/tasks",
        summary:
            "Main stretches into the shell's remaining height, so a full-height layout ends flush with the viewport.",
    },
};

const modeByRoute: Record<AppShellPlaygroundRoute, Mode> = {
    "/accounts": "default",
    "/transactions": "contained",
    "/tasks": "fullPage",
};

export const AppShellPlayground = () => {
    const [mode, setMode] = useState<Mode>("default");
    const [hasNavigation, setHasNavigation] = useState(true);
    const [isNavigationOpen, setIsNavigationOpen] = useState(true);
    const [showSubNavigation, setShowSubNavigation] = useState(true);
    const [hasBanner, setHasBanner] = useState(true);
    const [hasAside, setHasAside] = useState(false);

    const activeMode = modeDetails[mode];
    const isNavigationExpanded = hasNavigation && isNavigationOpen;

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Layout mode</h2>
                        <VStack gap={12}>
                            <ToggleGroup.Root<Mode>
                                value={mode}
                                onValueChange={(nextMode) => {
                                    if (nextMode) {
                                        setMode(nextMode);
                                    }
                                }}
                            >
                                {modes.map((option) => (
                                    <ToggleGroup.Item
                                        key={option}
                                        value={option}
                                    >
                                        {modeDetails[option].label}
                                    </ToggleGroup.Item>
                                ))}
                            </ToggleGroup.Root>
                            <p className={layoutStyles.hint}>
                                Mirrors <code>{activeMode.route}</code>.{" "}
                                {activeMode.summary}
                            </p>
                        </VStack>
                    </section>
                    <section>
                        <h2>Slots</h2>
                        <VStack gap={12}>
                            <div className={layoutStyles.booleanGrid}>
                                <BooleanControl
                                    label="Navigation"
                                    checked={hasNavigation}
                                    onChange={setHasNavigation}
                                />
                                <BooleanControl
                                    label="Navigation open"
                                    checked={isNavigationExpanded}
                                    disabled={!hasNavigation}
                                    onChange={setIsNavigationOpen}
                                />
                                <BooleanControl
                                    label="Sub navigation"
                                    checked={showSubNavigation}
                                    onChange={setShowSubNavigation}
                                />
                                <BooleanControl
                                    label="Banner"
                                    checked={hasBanner}
                                    onChange={setHasBanner}
                                />
                                <BooleanControl
                                    label="Aside"
                                    checked={hasAside}
                                    onChange={setHasAside}
                                />
                            </div>
                            <p className={layoutStyles.hint}>
                                AppShell always renders the{" "}
                                <code>data-sub-navigation</code> element that
                                app-tier providers portal into, so page
                                sub-navigation can mount before the row is
                                revealed. The toggle only sizes the row and this
                                demo's content.
                            </p>
                        </VStack>
                    </section>
                    <p className={layoutStyles.hint}>
                        The frame renders the shell 1:1 — full width and exactly
                        one viewport tall — so nothing is scaled. The page stays
                        the only scroll container. The navigation reserves its
                        width from 1200px up; below that the navigation overlays
                        the content column, exactly as the app's drawer does.
                    </p>
                </>
            }
        >
            <ShowcasePreview
                summary={`${activeMode.label} · ${activeMode.route}`}
                alignment="stretch"
                width="full"
                output={JSON.stringify(
                    {
                        componentProps: {
                            showSubNavigation,
                            contained: mode === "contained",
                            fullPage: mode === "fullPage",
                        },
                        showcaseState: {
                            hasNavigation,
                            isNavigationOpen: isNavigationExpanded,
                            hasBanner,
                            hasAside,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={styles.frame}>
                    <AppShell
                        navigation={
                            hasNavigation ? (
                                <AppShellPlaygroundNavigation
                                    isOpen={isNavigationExpanded}
                                    activeRoute={activeMode.route}
                                    onSelectRoute={(route) =>
                                        setMode(modeByRoute[route])
                                    }
                                />
                            ) : null
                        }
                        header={
                            <header className={styles.demoHeader}>
                                <span className={styles.demoHeaderContent}>
                                    <span>Breadcrumbs</span>
                                    <span>Header actions</span>
                                </span>
                                <span className={styles.slotLabel}>header</span>
                            </header>
                        }
                        banner={
                            hasBanner ? (
                                <div className={styles.demoBanner}>
                                    Organization banner
                                    <span className={styles.slotLabel}>
                                        banner
                                    </span>
                                </div>
                            ) : null
                        }
                        subNavigation={
                            showSubNavigation ? (
                                <div className={styles.demoSubNavigation}>
                                    Page sub-navigation
                                    <span className={styles.slotLabel}>
                                        subNavigation
                                    </span>
                                </div>
                            ) : null
                        }
                        showSubNavigation={showSubNavigation}
                        aside={
                            hasAside ? (
                                <aside className={styles.demoAside}>
                                    <span className={styles.slotLabel}>
                                        aside
                                    </span>
                                    <p>
                                        Chat and other asides overlay the shell
                                        from the right instead of taking space
                                        in the content column.
                                    </p>
                                </aside>
                            ) : null
                        }
                        contained={mode === "contained"}
                        fullPage={mode === "fullPage"}
                    >
                        <div className={styles.demoMain}>
                            <div className={styles.demoMainHeading}>
                                <span className={styles.demoMainCaption}>
                                    main container
                                </span>
                                <span className={styles.slotLabel}>
                                    children
                                </span>
                            </div>
                            <div className={styles.demoMainSurface}>
                                <h3>{activeMode.label} main region</h3>
                                <p>{activeMode.summary}</p>
                            </div>
                        </div>
                    </AppShell>
                    {mode === "contained" ? (
                        <div className={styles.containedReserve}>
                            <span>
                                Contained shell ends here · calc(100vh - 160px)
                            </span>
                        </div>
                    ) : null}
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
